/**
 * Section Component Utils
 *
 * Utilities for section component including format and parse functions for layout, orientation, and background layers.
 */

import type { DefaultVariableResult } from '@makyoapp/makyo-common'

import { formatColorFormat, formatFromVariableSelect } from '../../format-utils'
import { parseColorFormat, parseResponsiveFormat, parseSelectFormat } from '../../parser-utils'
import type {
  AppBreakpoint,
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  GradientDirection,
  ResponsiveValue,
} from '../../types'
import type {
  BackgroundColorLayer,
  BackgroundEffectLayer,
  BackgroundEffectType,
  BackgroundImageLayer,
  BackgroundLayer,
  BackgroundMaskLayer,
  BackgroundOverlayLayer,
  BackgroundPatternLayer,
  BackgroundVideoLayer,
} from './section.types'

// --------------------------------------------------------------------------------------//
//                                Section Formmater                                      //
// --------------------------------------------------------------------------------------//

/**
 * Format background layers back to string array format
 * Converts BackgroundLayer[] back to ["solid:primary-40", "image:url", "overlay:#000:0.5"] format
 */
export function formatBackgroundLayers(layers: BackgroundLayer[] | undefined): string[] | undefined {
  if (!layers || layers.length === 0) {
    return undefined
  }

  const result: string[] = []

  for (const layer of layers) {
    if (layer.type === 'color') {
      const colorLayer = layer as BackgroundColorLayer

      // Map mode values to ColorType
      const mapModeToColorType = (mode: string): ColorType => {
        if (mode === 'No Color') return 'transparent'
        if (mode === 'Solid Color') return 'solid'
        if (mode === 'Gradient') return 'gradient'
        if (mode === 'Adaptive') return 'adaptive'
        return 'solid' // default fallback
      }

      // Handle mode as string (legacy format) or object with light/dark
      const modeValue = colorLayer.values.mode
      let modeLight: string
      let modeDark: string

      if (typeof modeValue === 'string') {
        // Legacy format: mode is a string (e.g., "No Color")
        modeLight = modeValue
        modeDark = modeValue
      } else {
        // Modern format: mode is an object with light/dark properties
        modeLight = modeValue?.light || 'No Color'
        modeDark = modeValue?.dark || 'No Color'
      }

      // Convert mode strings to ColorType
      const colorTypeLight = mapModeToColorType(modeLight)
      const colorTypeDark = mapModeToColorType(modeDark)

      // Extract color values, removing "adaptive:" prefix if present
      const colorLight = colorLayer.values.color?.light || 'primary-40'
      const colorDark = colorLayer.values.color?.dark || colorLight
      const cleanColorLight = colorLight.startsWith('adaptive:') ? colorLight.substring(9) : colorLight
      const cleanColorDark = colorDark.startsWith('adaptive:') ? colorDark.substring(9) : colorDark

      // Extract gradient values
      const gradientStartLight = colorLayer.values.gradientStart?.light || cleanColorLight
      const gradientEndLight = colorLayer.values.gradientEnd?.light || 'primary-80'
      const gradientStartDark = colorLayer.values.gradientStart?.dark || cleanColorDark
      const gradientEndDark = colorLayer.values.gradientEnd?.dark || 'primary-80'

      // Extract direction and convert spaces to dashes
      const directionRaw = colorLayer.values.direction || 'to right'
      const directionLight = directionRaw.replace(/\s+/g, '-') as GradientDirection
      const directionDark = directionRaw.replace(/\s+/g, '-') as GradientDirection

      // Build color settings object for formatColorFormat
      const colorSettings: {
        colorType: ColorAdaptiveValue<ColorType>
        colorSolid: ColorAdaptiveValue<ColorVariant>
        colorStartGradient?: ColorAdaptiveValue<ColorVariant>
        colorEndGradient?: ColorAdaptiveValue<ColorVariant>
        colorGradientDirection?: ColorAdaptiveValue<GradientDirection>
      } = {
        colorType: {
          light: colorTypeLight,
          dark: colorTypeDark,
        },
        colorSolid: {
          light: cleanColorLight as ColorVariant,
          dark: cleanColorDark as ColorVariant,
        },
      }

      // Add gradient properties if needed
      if (colorTypeLight === 'gradient' || colorTypeDark === 'gradient') {
        colorSettings.colorStartGradient = {
          light: gradientStartLight as ColorVariant,
          dark: gradientStartDark as ColorVariant,
        }
        colorSettings.colorEndGradient = {
          light: gradientEndLight as ColorVariant,
          dark: gradientEndDark as ColorVariant,
        }
        colorSettings.colorGradientDirection = {
          light: directionLight,
          dark: directionDark,
        }
      }

      // Format opacity if present (only include if not default 100)
      const formatOpacity = (opacity: number | undefined) => {
        return opacity !== undefined && opacity !== 100 ? `:${opacity}` : ''
      }

      const opacityLight = colorLayer.values.colorOpacity?.light
      const opacityDark = colorLayer.values.colorOpacity?.dark

      // Handle cases where one mode is transparent and the other is not
      if (colorTypeLight === 'transparent' && colorTypeDark === 'transparent') {
        // Both transparent
        result.push('transparent')
      } else if (colorTypeLight === 'transparent' || colorTypeDark === 'transparent') {
        // One mode is transparent - format manually
        if (colorTypeLight === 'transparent') {
          // Light: transparent, Dark: has color
          // Format the dark mode color using formatColorFormat with only dark mode
          const darkColorSettings = {
            colorType: { light: colorTypeDark, dark: colorTypeDark },
            colorSolid: { light: cleanColorDark as ColorVariant, dark: cleanColorDark as ColorVariant },
            ...(colorTypeDark === 'gradient' && {
              colorStartGradient: { light: gradientStartDark as ColorVariant, dark: gradientStartDark as ColorVariant },
              colorEndGradient: { light: gradientEndDark as ColorVariant, dark: gradientEndDark as ColorVariant },
              colorGradientDirection: { light: directionDark, dark: directionDark },
            }),
          }
          const darkFormat = formatColorFormat(darkColorSettings) || ''
          const opacityPart = formatOpacity(opacityDark)
          result.push(`color:transparent ${darkFormat}${opacityPart}`)
        } else {
          // Light: has color, Dark: transparent
          // Format the light mode color using formatColorFormat with only light mode
          const lightColorSettings = {
            colorType: { light: colorTypeLight, dark: colorTypeLight },
            colorSolid: { light: cleanColorLight as ColorVariant, dark: cleanColorLight as ColorVariant },
            ...(colorTypeLight === 'gradient' && {
              colorStartGradient: {
                light: gradientStartLight as ColorVariant,
                dark: gradientStartLight as ColorVariant,
              },
              colorEndGradient: { light: gradientEndLight as ColorVariant, dark: gradientEndLight as ColorVariant },
              colorGradientDirection: { light: directionLight, dark: directionLight },
            }),
          }
          const lightFormat = formatColorFormat(lightColorSettings) || ''
          const opacityPart = formatOpacity(opacityLight)
          result.push(`color:${lightFormat}${opacityPart} transparent`)
        }
      } else {
        // Both modes have color - use formatColorFormat
        const colorFormat = formatColorFormat(colorSettings)

        if (!colorFormat) {
          // If formatColorFormat returns undefined, skip this layer
          continue
        }

        // Add opacity and "color:" prefix
        // If format contains space (different light/dark), we need to add opacity to both parts
        if (colorFormat.includes(' ')) {
          const parts = colorFormat.split(' ')
          const lightPart = parts[0]
          const darkPart = parts[1]
          const lightOpacityPart = formatOpacity(opacityLight)
          const darkOpacityPart = formatOpacity(opacityDark)
          result.push(`color:${lightPart}${lightOpacityPart} ${darkPart}${darkOpacityPart}`)
        } else {
          const opacityPart = formatOpacity(opacityLight)
          result.push(`color:${colorFormat}${opacityPart}`)
        }
      }
    } else if (layer.type === 'image') {
      const imageLayer = layer as BackgroundImageLayer
      const url = formatFromVariableSelect(imageLayer.values.url)
      if (url) {
        // formatFromVariableSelect already includes "image:" prefix for Image flag
        // Remove it if present since we're adding "image:" prefix here
        // formatFromVariableSelect may return "image:<id>:<url>" format if ID is present
        let urlWithoutPrefix = url.startsWith('image:') ? url.substring(6) : url
        const parallax = imageLayer.values.parallax ? ':parallax' : ''
        result.push(`image:${urlWithoutPrefix}${parallax}`)
      }
    } else if (layer.type === 'video') {
      const videoLayer = layer as BackgroundVideoLayer
      const url = formatFromVariableSelect(videoLayer.values.url)
      if (url) {
        const loop = videoLayer.values.loop ? ':loop' : ''
        const muted = videoLayer.values.muted ? ':muted' : ''
        result.push(`video:${url}${loop}${muted}`)
      }
    } else if (layer.type === 'overlay') {
      const overlayLayer = layer as BackgroundOverlayLayer
      if (overlayLayer.values.enabled) {
        const opacity = Array.isArray(overlayLayer.values.opacity)
          ? overlayLayer.values.opacity[0]
          : overlayLayer.values.opacity
        result.push(`overlay:${overlayLayer.values.color}:${opacity}:enabled`)
      }
    } else if (layer.type === 'pattern') {
      const patternLayer = layer as BackgroundPatternLayer
      if (patternLayer.values.type) {
        result.push(`pattern:${patternLayer.values.type}:${patternLayer.values.opacity}:${patternLayer.values.color}`)
      }
    } else if (layer.type === 'effect') {
      const effectLayer = layer as BackgroundEffectLayer
      if (effectLayer.values.effectType && effectLayer.values.effectType !== 'none') {
        const colors = effectLayer.values.effectColors.join(',')
        const bgColor = effectLayer.values.effectBackgroundColor
        const speed = effectLayer.values.effectSpeed
        const opacity = effectLayer.values.effectOpacity
        result.push(`effect:${effectLayer.values.effectType}:${colors}:${bgColor}:${speed}:${opacity}`)
      }
    } else if (layer.type === 'mask') {
      const maskLayer = layer as BackgroundMaskLayer
      // Format mask layer with responsive values
      // Format: mask:<type>:<opacity>:<size>:<rotate>:<inverted>:<xpos>:<ypos>
      // With breakpoint prefixes for responsive values
      // Find first available breakpoint value (phone is preferred)
      const breakpoints: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
      let phoneMask = maskLayer.values.phone
      if (!phoneMask) {
        // If phone doesn't exist, find first available breakpoint
        for (const bp of breakpoints) {
          if (maskLayer.values[bp]) {
            phoneMask = maskLayer.values[bp]
            break
          }
        }
      }
      if (phoneMask) {
        // Format mask type: strip /mask/ prefix and .svg suffix (e.g., "/mask/form.svg" -> "form")
        const formatMaskType = (maskType: string): string => {
          if (!maskType) return ''
          // Remove /mask/ prefix if present
          let shortType = maskType.startsWith('/mask/') ? maskType.substring(6) : maskType
          // Remove .svg suffix if present
          if (shortType.endsWith('.svg')) {
            shortType = shortType.substring(0, shortType.length - 4)
          }
          return shortType
        }

        const type = formatMaskType(phoneMask.type || '')
        const opacity = Array.isArray(phoneMask.opacity) ? phoneMask.opacity[0] : phoneMask.opacity
        const size = Array.isArray(phoneMask.size) ? phoneMask.size[0] : phoneMask.size
        const rotate = Array.isArray(phoneMask.rotate) ? phoneMask.rotate[0] : phoneMask.rotate
        const inverted = phoneMask.inverted ? '1' : '0'
        const xpos = phoneMask.xpos ?? 50
        const ypos = phoneMask.ypos ?? 50

        // Check if all breakpoints have the same values
        const breakpoints: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
        const allSame = breakpoints.every((bp) => {
          const bpMask = maskLayer.values[bp]
          if (!bpMask) return true
          return (
            formatMaskType(bpMask.type) === type &&
            (Array.isArray(bpMask.opacity) ? bpMask.opacity[0] : bpMask.opacity) === opacity &&
            (Array.isArray(bpMask.size) ? bpMask.size[0] : bpMask.size) === size &&
            (Array.isArray(bpMask.rotate) ? bpMask.rotate[0] : bpMask.rotate) === rotate &&
            bpMask.inverted === phoneMask.inverted &&
            bpMask.xpos === xpos &&
            bpMask.ypos === ypos
          )
        })

        if (allSame) {
          // All breakpoints same - simple format
          result.push(`mask:${type}:${opacity}:${size}:${rotate}:${inverted}:${xpos}:${ypos}`)
        } else {
          // Different values per breakpoint - format with prefixes
          const parts: string[] = []
          const prefixes: Record<AppBreakpoint, string> = {
            phone: '',
            'phone-landscape': 'pl:',
            tablet: 'ta:',
            'tablet-landscape': 'tl:',
            desktop: 'de:',
            tv: 'tv:',
          }

          // Output phone first (no prefix)
          const phoneType = formatMaskType(phoneMask.type || '')
          const phoneOpacity = Array.isArray(phoneMask.opacity) ? phoneMask.opacity[0] : phoneMask.opacity
          const phoneSize = Array.isArray(phoneMask.size) ? phoneMask.size[0] : phoneMask.size
          const phoneRotate = Array.isArray(phoneMask.rotate) ? phoneMask.rotate[0] : phoneMask.rotate
          const phoneInverted = phoneMask.inverted ? '1' : '0'
          const phoneXpos = phoneMask.xpos ?? 50
          const phoneYpos = phoneMask.ypos ?? 50
          const phoneFormatted = `${phoneType}:${phoneOpacity}:${phoneSize}:${phoneRotate}:${phoneInverted}:${phoneXpos}:${phoneYpos}`
          parts.push(phoneFormatted)

          // Output other breakpoints only if they differ from phone
          let lastMask = phoneMask
          for (const bp of breakpoints.slice(1)) {
            const bpMask = maskLayer.values[bp]
            if (!bpMask) {
              // Inherit from previous breakpoint
              continue
            }

            const bpType = formatMaskType(bpMask.type || phoneType)
            const bpOpacity = Array.isArray(bpMask.opacity) ? bpMask.opacity[0] : bpMask.opacity
            const bpSize = Array.isArray(bpMask.size) ? bpMask.size[0] : bpMask.size
            const bpRotate = Array.isArray(bpMask.rotate) ? bpMask.rotate[0] : bpMask.rotate
            const bpInverted = bpMask.inverted ? '1' : '0'
            const bpXpos = bpMask.xpos ?? 50
            const bpYpos = bpMask.ypos ?? 50

            // Check if different from previous breakpoint
            const lastType = formatMaskType(lastMask.type || phoneType)
            const lastOpacity = Array.isArray(lastMask.opacity) ? lastMask.opacity[0] : lastMask.opacity
            const lastSize = Array.isArray(lastMask.size) ? lastMask.size[0] : lastMask.size
            const lastRotate = Array.isArray(lastMask.rotate) ? lastMask.rotate[0] : lastMask.rotate
            const lastInverted = lastMask.inverted ? '1' : '0'
            const lastXpos = lastMask.xpos ?? 50
            const lastYpos = lastMask.ypos ?? 50

            if (
              bpType !== lastType ||
              bpOpacity !== lastOpacity ||
              bpSize !== lastSize ||
              bpRotate !== lastRotate ||
              bpInverted !== lastInverted ||
              bpXpos !== lastXpos ||
              bpYpos !== lastYpos
            ) {
              const formatted = `${bpType}:${bpOpacity}:${bpSize}:${bpRotate}:${bpInverted}:${bpXpos}:${bpYpos}`
              parts.push(`${prefixes[bp]}${formatted}`)
              lastMask = bpMask
            } else {
              lastMask = bpMask
            }
          }

          result.push(`mask:${parts.join(' ')}`)
        }
      }
    }
  }

  return result.length > 0 ? result : undefined
}

// --------------------------------------------------------------------------------------//
//                                Section Parser                                         //
// --------------------------------------------------------------------------------------//

/**
 * Parse layout format string
 * Format: "cols:<width>[:<width>...]"
 * Example: "cols:100" or "cols:50:50" or "cols:100 ta:cols:50:50"
 * Converts percentages to 1/12 parts (100% = 12, 50% = 6, etc.)
 */
export function parseLayoutFormat(format: string): ResponsiveValue<number[]> {
  return parseResponsiveFormat(format, (value) => {
    if (!value.startsWith('cols:')) {
      return [12] // Default to single column (100% = 12)
    }

    const colsPart = value.substring(5) // Remove "cols:"
    const widths = colsPart.split(':').map((w) => {
      const percent = parseInt(w, 10)
      // Convert percentage to 1/12 parts: 100% = 12, 50% = 6, 33% = 4, etc.
      return Math.round((percent / 100) * 12)
    })
    return widths.length > 0 ? widths : [12]
  })
}

/**
 * Parse orientation format string
 * Format: "h" | "v" or responsive: "v de:h" | "v ta:h de:v"
 * Example: "h" or "v" or "v de:h" or "v ta:h de:v"
 * Supports all breakpoint prefixes: ph, pl, ta, tl, de, tv
 */
export function parseOrientationFormat(format: string): ResponsiveValue<string> {
  return parseResponsiveFormat(format, (value) => {
    // Map short form to full form
    if (value === 'h') return 'horizontal'
    if (value === 'v') return 'vertical'
    // If already full form, return as is
    if (value === 'horizontal' || value === 'vertical') return value
    // Default to vertical
    return 'vertical'
  })
}

/**
 * Helper to find or create a layer of specific type from template layers
 */
function findOrCreateLayer<T extends BackgroundLayer>(type: T['type'], templateLayers: BackgroundLayer[]): T | null {
  const existing = templateLayers.find((l) => l.type === type) as T | undefined
  if (existing) {
    return { ...existing } as T
  }
  return null
}

/**
 * Map color type string to mode string
 */
function mapColorTypeToMode(type: string): string {
  if (type === 'solid') return 'Solid Color'
  if (type === 'gradient') return 'Gradient'
  if (type === 'adaptive') return 'Adaptive'
  if (type === 'transparent') return 'No Color'
  return type
}

/**
 * Parse color layer from format string
 * Format: "solid:primary-40" or "solid:primary-40:10" (with opacity) or "color:solid:primary-40" or "color:adaptive:primary-80:8 primary-100:10" or "transparent" or "color:transparent"
 */
function parseColorLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundColorLayer | null; id: number } {
  // Handle multiple gradient formats: "gradient:start|end:dir gradient:darkStart|darkEnd:dir"
  // Convert to adaptive format for parsing
  if (format.includes('gradient:') && format.split('gradient:').length > 2) {
    // Multiple gradient parts - convert to adaptive format
    const parts = format.split(/\s+/).filter((p) => p.startsWith('gradient:'))
    if (parts.length === 2) {
      const lightPart = parts[0].substring(9) // Remove "gradient:"
      const darkPart = parts[1].substring(9) // Remove "gradient:"
      format = `color:adaptive:${lightPart} ${darkPart}`
    } else if (parts.length === 1) {
      // Single gradient - convert to simple format
      const gradientPart = parts[0].substring(9) // Remove "gradient:"
      format = `color:gradient:${gradientPart}`
    }
  }

  // Remove "color:" prefix if present
  let colorPart = format.startsWith('color:') ? format.substring(6) : format

  // Extract opacity from format string (format: "solid:primary-40:10" or "adaptive:primary-80:8 primary-100:10")
  let opacityLight: number | undefined = undefined
  let opacityDark: number | undefined = undefined

  // Handle adaptive format with opacity: "adaptive:primary-80:8 primary-100:10"
  if (colorPart.startsWith('adaptive:')) {
    const adaptivePart = colorPart.substring(9) // Remove "adaptive:"
    const parts = adaptivePart.split(' ')
    const cleanParts: string[] = []

    if (parts.length >= 1 && parts[0]) {
      // Extract light color and opacity
      const lightParts = parts[0].split(':')
      if (lightParts.length >= 2 && !isNaN(Number(lightParts[lightParts.length - 1]))) {
        // Last part is opacity
        opacityLight = Number(lightParts[lightParts.length - 1])
        cleanParts.push(lightParts.slice(0, -1).join(':'))
      } else {
        cleanParts.push(parts[0])
      }
    }

    if (parts.length >= 2 && parts[1]) {
      // Extract dark color and opacity
      const darkParts = parts[1].split(':')
      if (darkParts.length >= 2 && !isNaN(Number(darkParts[darkParts.length - 1]))) {
        // Last part is opacity
        opacityDark = Number(darkParts[darkParts.length - 1])
        cleanParts.push(darkParts.slice(0, -1).join(':'))
      } else {
        cleanParts.push(parts[1])
      }
    }

    colorPart = `adaptive:${cleanParts.join(' ')}`
  } else if (colorPart.includes(':') && !colorPart.startsWith('gradient:') && !colorPart.startsWith('transparent')) {
    // Handle solid format with opacity: "solid:primary-40:10"
    const parts = colorPart.split(':')
    if (parts.length >= 3 && !isNaN(Number(parts[parts.length - 1]))) {
      // Last part is opacity (only if it's a number and we have at least 3 parts)
      opacityLight = Number(parts[parts.length - 1])
      opacityDark = opacityLight
      colorPart = parts.slice(0, -1).join(':')
    }
  }

  const colorParsed = parseColorFormat(colorPart)
  const colorLayer = findOrCreateLayer<BackgroundColorLayer>('color', templateLayers)

  if (!colorLayer) {
    return { layer: null, id: nextId }
  }

  const isLightTransparent = colorParsed.colorType.light === 'transparent'
  const isDarkTransparent = colorParsed.colorType.dark === 'transparent'
  const isTransparent = isLightTransparent && isDarkTransparent
  const isGradient = colorParsed.colorType.light === 'gradient' || colorParsed.colorType.dark === 'gradient'

  colorLayer.values.mode = {
    light: mapColorTypeToMode(colorParsed.colorType.light),
    dark: mapColorTypeToMode(colorParsed.colorType.dark),
  }

  // Set color values - always set them, mode will determine if they're used
  // For transparent modes, the color values are set but won't be used (mode is "No Color")
  if (!isTransparent) {
    colorLayer.values.color = colorParsed.colorSolid
    colorLayer.values.gradientStart = colorParsed.colorStartGradient
    colorLayer.values.gradientEnd = colorParsed.colorEndGradient

    // Set opacity if provided (default to 100 if not specified)
    // For transparent modes, opacity is not used but we still set it for consistency
    if (opacityLight !== undefined || opacityDark !== undefined) {
      colorLayer.values.colorOpacity = {
        light: opacityLight !== undefined ? opacityLight : 100,
        dark: opacityDark !== undefined ? opacityDark : opacityLight !== undefined ? opacityLight : 100,
      }
    }
  }

  if (isGradient) {
    colorLayer.values.gradientStartOpacity = {
      light: [100],
      dark: [100],
    }
    colorLayer.values.gradientEndOpacity = {
      light: [100],
      dark: [100],
    }

    const gradientDir = colorParsed.colorGradientDirection
    if (gradientDir) {
      const lightDir = gradientDir.light || gradientDir.dark
      colorLayer.values.direction = lightDir?.replace(/-/g, ' ') || 'to right'
    }
  }

  colorLayer.id = nextId
  return { layer: colorLayer, id: nextId + 1 }
}

/**
 * Parse image layer from format string
 * Format: "image:url" or "image:url:parallax" or variable select format
 * If URL doesn't start with a select prefix, defaults to "image:<url>" (flag 128)
 */
function parseImageLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundImageLayer | null; id: number } {
  const imagePart = format.substring(6) // Remove "image:"
  let urlPart = imagePart
  let parallax = false

  // Check if there's :parallax or :true at the end (but not :// which is part of URL)
  if (imagePart.endsWith(':parallax')) {
    parallax = true
    urlPart = imagePart.substring(0, imagePart.length - 9)
  } else if (imagePart.endsWith(':true') && !imagePart.endsWith('://true')) {
    // Only treat as parallax if it's not part of the URL protocol
    const beforeTrue = imagePart.substring(0, imagePart.length - 5)
    if (!beforeTrue.includes('://')) {
      parallax = true
      urlPart = beforeTrue
    }
  }

  // Parse URL as variable select format
  // If it doesn't start with a select prefix, default to image: format (flag 128)
  // Check for variable select prefixes (excluding http/https URLs which contain ://)
  const variableSelectPrefixes = ['text:', 'sc:', 'cr:', 'image:', 'file:', 'profile:', 'icon:', 'rc:', 'dt:', 'gv:']
  const variableSelectKeywords = ['pc', 'pt', 'pl', 'ps', 'cdt', 'sid', 'uid']
  const isVariableSelect =
    (!urlPart.includes('://') && variableSelectPrefixes.some((prefix) => urlPart.startsWith(prefix))) ||
    variableSelectKeywords.includes(urlPart)

  // If urlPart already starts with "image:" (from format string), use it directly
  // Otherwise, if it's a URL (contains ://), wrap it in image: format
  // If it's a variable select, parse it directly
  let url: DefaultVariableResult
  if (urlPart.startsWith('image:')) {
    // Already has image: prefix (shouldn't happen, but handle it)
    url = parseSelectFormat(urlPart)
  } else if (isVariableSelect) {
    url = parseSelectFormat(urlPart)
  } else {
    // URL or plain value - wrap in image: format (flag 128)
    url = parseSelectFormat(`image:${urlPart}`)
  }

  const imageLayer = findOrCreateLayer<BackgroundImageLayer>('image', templateLayers)
  if (!imageLayer) {
    return { layer: null, id: nextId }
  }

  imageLayer.id = nextId
  imageLayer.values.url = url
  imageLayer.values.parallax = parallax
  return { layer: imageLayer, id: nextId + 1 }
}

/**
 * Parse video layer from format string
 * Format: "video:url" or "video:url:loop:muted" or variable select format
 * If URL doesn't start with a select prefix, defaults to "text:<url>" (flag 2048)
 */
function parseVideoLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundVideoLayer | null; id: number } {
  const videoPart = format.substring(6) // Remove "video:"
  let urlPart = videoPart
  let loop = true // default
  let muted = true // default

  // Parse from the end, looking for :loop and :muted flags
  // Check for :loop:muted pattern first (both flags)
  if (videoPart.endsWith(':loop:muted')) {
    loop = true
    muted = true
    urlPart = videoPart.substring(0, videoPart.length - 11)
  } else if (videoPart.endsWith(':muted:loop')) {
    // Handle reversed order
    loop = true
    muted = true
    urlPart = videoPart.substring(0, videoPart.length - 11)
  } else if (videoPart.endsWith(':muted')) {
    muted = true
    urlPart = videoPart.substring(0, videoPart.length - 6)
  } else if (videoPart.endsWith(':loop')) {
    loop = true
    urlPart = videoPart.substring(0, videoPart.length - 5)
  } else {
    // Check for :loop: or :muted: in the middle (but not :// which is part of URL)
    const loopMutedPattern = /:(loop|muted)(?::(loop|muted))?$/
    const match = videoPart.match(loopMutedPattern)
    if (match) {
      const firstFlag = match[1]
      const secondFlag = match[2]

      if (firstFlag === 'loop') loop = true
      if (firstFlag === 'muted') muted = true
      if (secondFlag === 'loop') loop = true
      if (secondFlag === 'muted') muted = true

      // Remove the matched flags from the end
      urlPart = videoPart.substring(0, match.index!)
    }
  }

  // Parse URL as variable select format
  // Format can be:
  //   - video:text:https://... (explicit variable select format)
  //   - video:https://... (simple URL, defaults to text: format)
  //   - video:sc:table:col:row (variable select format)
  //   - video:cr:colId (variable select format)
  //
  // According to docs: Simple URLs default to text: format (flag 2048)
  // So video:https://... should be parsed as video:text:https://...

  const variableSelectPrefixes = ['text:', 'sc:', 'cr:', 'image:', 'file:', 'profile:', 'icon:', 'rc:', 'dt:', 'gv:']
  const variableSelectKeywords = ['pc', 'pt', 'pl', 'ps', 'cdt', 'sid', 'uid']

  // Check if urlPart starts with a variable select prefix first
  // This handles formats like "text:https://..." or "sc:table:col:row"
  const hasVariableSelectPrefix = variableSelectPrefixes.some((prefix) => urlPart.startsWith(prefix))
  const isVariableSelectKeyword = variableSelectKeywords.includes(urlPart)

  // If it starts with a prefix or is a keyword, parse it directly as variable select
  // Otherwise, if it's a URL (contains ://), wrap it in text: format (flag 2048)
  // If it's neither, it might be a keyword or plain value - wrap in text: format
  let url: DefaultVariableResult
  if (hasVariableSelectPrefix || isVariableSelectKeyword) {
    // Already has variable select format - parse directly
    url = parseSelectFormat(urlPart)
  } else if (urlPart.includes('://')) {
    // Plain URL without prefix - wrap in text: format (flag 2048)
    url = parseSelectFormat(`text:${urlPart}`)
  } else {
    // Not a URL and not a keyword - might be a plain value, wrap in text: format
    url = parseSelectFormat(`text:${urlPart}`)
  }

  const videoLayer = findOrCreateLayer<BackgroundVideoLayer>('video', templateLayers)
  if (!videoLayer) {
    return { layer: null, id: nextId }
  }

  videoLayer.id = nextId
  videoLayer.values.url = url
  videoLayer.values.loop = loop
  videoLayer.values.muted = muted
  return { layer: videoLayer, id: nextId + 1 }
}

/**
 * Parse overlay layer from format string
 * Format: "overlay:#000000:0.5" or "overlay:#000000:0.5:enabled"
 */
function parseOverlayLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundOverlayLayer | null; id: number } {
  const overlayPart = format.substring(8) // Remove "overlay:"
  const parts = overlayPart.split(':')
  const color = parts[0] || '#000000'
  const opacity = parseFloat(parts[1] || '0.5')
  const enabled = parts[2] === 'enabled' || parts[2] === 'true' || parts.length === 2

  const overlayLayer = findOrCreateLayer<BackgroundOverlayLayer>('overlay', templateLayers)
  if (!overlayLayer) {
    return { layer: null, id: nextId }
  }

  overlayLayer.id = nextId
  overlayLayer.values.color = color
  overlayLayer.values.opacity = opacity
  overlayLayer.values.enabled = enabled
  return { layer: overlayLayer, id: nextId + 1 }
}

/**
 * Parse pattern layer from format string
 * Format: "pattern:Circle1:0.1:primary-10"
 */
function parsePatternLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundPatternLayer | null; id: number } {
  const patternPart = format.substring(8) // Remove "pattern:"
  const parts = patternPart.split(':')
  const type = parts[0] || ''
  const opacity = parseFloat(parts[1] || '0.1')
  const color = (parts[2] || 'primary-10') as ColorVariant

  const patternLayer = findOrCreateLayer<BackgroundPatternLayer>('pattern', templateLayers)
  if (!patternLayer) {
    return { layer: null, id: nextId }
  }

  patternLayer.id = nextId
  patternLayer.values.type = type
  patternLayer.values.opacity = opacity
  patternLayer.values.color = color
  return { layer: patternLayer, id: nextId + 1 }
}

/**
 * Parse effect layer from format string
 * Format: "effect:gradient:primary-40,secondary-60,tertiary-80:neutral-100:100:100"
 * Colors and background color use ColorVariant format (e.g., primary-40, neutral-100) instead of hex codes
 */
function parseEffectLayer(format: string, nextId: number): { layer: BackgroundEffectLayer; id: number } {
  const effectPart = format.substring(7) // Remove "effect:"
  const parts = effectPart.split(':')
  const effectTypeStr = parts[0] || 'gradient'
  // Validate and cast to BackgroundEffectType, default to 'gradient' if invalid
  const effectType: BackgroundEffectType = (
    [
      'none',
      'gradient',
      'animated-gradient',
      'beams',
      'wavy',
      'sparkles',
      'flicker-grid',
      'meteors',
      'stars',
      'boxes',
      'aurora',
      'paths',
      'fluid-gradient',
      'shader-clouds',
      'orbs',
      'dot-matrix',
      'spotlight',
      'noise',
      'mesh-gradient',
      'ripple-waves',
      'floating-bubbles',
      'neural-network',
      'matrix-rain',
      'particles-flow',
    ].includes(effectTypeStr)
      ? effectTypeStr
      : 'gradient'
  ) as BackgroundEffectType
  const colorsStr = parts[1] || ''
  const bgColor = (parts[2] || 'neutral-100') as ColorVariant
  const speed = parseInt(parts[3] || '100', 10)
  const opacity = parseInt(parts[4] || '100', 10)
  const effectColors = colorsStr.split(',').filter((c) => c.length > 0) as ColorVariant[]

  const effectLayer: BackgroundEffectLayer = {
    id: nextId,
    type: 'effect',
    values: {
      effectType,
      effectSpeed: speed,
      effectOpacity: opacity,
      effectColors,
      effectBackgroundColor: bgColor,
    },
  }
  return { layer: effectLayer, id: nextId + 1 }
}

/**
 * Parse mask layer from format string
 * Format: "mask:<type>:<opacity>:<size>:<rotate>:<inverted>:<xpos>:<ypos>" or with breakpoint prefixes
 * Example: "mask:/mask/form.svg:0.15:51:-15:1:50:50"
 * Example with responsive: "mask:/mask/form.svg:0.15:70:-15:1:50:50 ta:/mask/form.svg:0.15:51:-15:1:50:50"
 */
function parseMaskLayer(
  format: string,
  templateLayers: BackgroundLayer[],
  nextId: number
): { layer: BackgroundMaskLayer | null; id: number } {
  const maskPart = format.substring(5) // Remove "mask:"

  // Parse responsive format - split by breakpoint prefixes
  const breakpoints: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
  const prefixes: Record<AppBreakpoint, string> = {
    phone: '',
    'phone-landscape': 'pl:',
    tablet: 'ta:',
    'tablet-landscape': 'tl:',
    desktop: 'de:',
    tv: 'tv:',
  }

  const result: Partial<
    Record<
      AppBreakpoint,
      {
        type: string
        opacity: number
        size: number
        rotate: number
        inverted: boolean
        xpos: number
        ypos: number
      }
    >
  > = {}

  // Split by whitespace - each part may have a breakpoint prefix
  const parts = maskPart.trim().split(/\s+/)

  for (const part of parts) {
    let breakpoint: AppBreakpoint = 'phone'
    let valuePart = part

    // Check for breakpoint prefix (must check longer prefixes first to avoid partial matches)
    // Order matters: check longer prefixes first (e.g., 'phone-landscape' before 'phone')
    const prefixEntries = Object.entries(prefixes).sort((a, b) => b[1].length - a[1].length)
    for (const [bp, prefix] of prefixEntries) {
      if (part.startsWith(prefix)) {
        breakpoint = bp as AppBreakpoint
        valuePart = part.substring(prefix.length)
        break
      }
    }

    // Parse mask values: type:opacity:size:rotate:inverted:xpos:ypos
    // Parse mask type: add /mask/ prefix and .svg suffix (e.g., "form" -> "/mask/form.svg")
    const parseMaskType = (shortType: string): string => {
      if (!shortType) return ''
      // Handle special characters like # which don't need /mask/ prefix
      if (shortType === '#' || shortType.startsWith('#')) {
        return shortType
      }
      // Add /mask/ prefix if not present
      let fullType = shortType.startsWith('/mask/') ? shortType : `/mask/${shortType}`
      // Add .svg suffix if not present
      if (!fullType.endsWith('.svg')) {
        fullType = `${fullType}.svg`
      }
      return fullType
    }

    const values = valuePart.split(':')
    if (values.length >= 7) {
      const type = parseMaskType(values[0] || '')
      const opacity = parseFloat(values[1]) || 0.15
      const size = parseFloat(values[2]) || 50
      const rotate = parseFloat(values[3]) || 0
      const inverted = values[4] === '1'
      const xpos = parseFloat(values[5]) || 50
      const ypos = parseFloat(values[6]) || 50

      result[breakpoint] = {
        type,
        opacity,
        size,
        rotate,
        inverted,
        xpos,
        ypos,
      }
    }
  }

  // If no breakpoint-specific values, parse as phone (default)
  if (Object.keys(result).length === 0) {
    const parseMaskType = (shortType: string): string => {
      if (!shortType) return ''
      let fullType = shortType.startsWith('/mask/') ? shortType : `/mask/${shortType}`
      if (!fullType.endsWith('.svg')) {
        fullType = `${fullType}.svg`
      }
      return fullType
    }

    const values = maskPart.split(':')
    if (values.length >= 7) {
      const type = parseMaskType(values[0] || '')
      const opacity = parseFloat(values[1]) || 0.15
      const size = parseFloat(values[2]) || 50
      const rotate = parseFloat(values[3]) || 0
      const inverted = values[4] === '1'
      const xpos = parseFloat(values[5]) || 50
      const ypos = parseFloat(values[6]) || 50

      result.phone = {
        type,
        opacity,
        size,
        rotate,
        inverted,
        xpos,
        ypos,
      }
    }
  }

  // Apply inheritance: phone-landscape from phone, tablet from phone-landscape, etc.
  const phone = result.phone
  if (!phone) {
    return { layer: null, id: nextId }
  }

  const phoneLandscape = result['phone-landscape'] || phone
  const tablet = result.tablet || phoneLandscape
  const tabletLandscape = result['tablet-landscape'] || tablet
  const desktop = result.desktop || tabletLandscape
  const tv = result.tv || desktop

  // Try to find mask layer in template, or create a new one if it doesn't exist
  let maskLayer = findOrCreateLayer<BackgroundMaskLayer>('mask', templateLayers)
  if (!maskLayer) {
    // Create a new mask layer if it doesn't exist in template
    maskLayer = {
      id: nextId,
      type: 'mask',
      values: {
        phone: {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
        'phone-landscape': {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
        tablet: {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
        'tablet-landscape': {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
        desktop: {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
        tv: {
          type: '/mask/form.svg',
          opacity: [0.15],
          size: [50],
          rotate: [0],
          inverted: false,
          xpos: 50,
          ypos: 50,
        },
      },
    } as unknown as BackgroundMaskLayer
  }

  // Convert to format with arrays (as expected by the actual data structure)
  // Note: The type definition says opacity/rotate are numbers, but actual data uses arrays
  maskLayer.values = {
    phone: {
      type: phone.type,
      opacity: [phone.opacity] as any, // Actual data uses arrays
      size: (Array.isArray(phone.size) ? phone.size : [phone.size]) as any,
      rotate: [phone.rotate] as any, // Actual data uses arrays
      inverted: phone.inverted,
      xpos: phone.xpos,
      ypos: phone.ypos,
    },
    'phone-landscape': {
      type: phoneLandscape.type,
      opacity: [phoneLandscape.opacity] as any,
      size: (Array.isArray(phoneLandscape.size) ? phoneLandscape.size : [phoneLandscape.size]) as any,
      rotate: [phoneLandscape.rotate] as any,
      inverted: phoneLandscape.inverted,
      xpos: phoneLandscape.xpos,
      ypos: phoneLandscape.ypos,
    },
    tablet: {
      type: tablet.type,
      opacity: [tablet.opacity] as any,
      size: (Array.isArray(tablet.size) ? tablet.size : [tablet.size]) as any,
      rotate: [tablet.rotate] as any,
      inverted: tablet.inverted,
      xpos: tablet.xpos,
      ypos: tablet.ypos,
    },
    'tablet-landscape': {
      type: tabletLandscape.type,
      opacity: [tabletLandscape.opacity] as any,
      size: (Array.isArray(tabletLandscape.size) ? tabletLandscape.size : [tabletLandscape.size]) as any,
      rotate: [tabletLandscape.rotate] as any,
      inverted: tabletLandscape.inverted,
      xpos: tabletLandscape.xpos,
      ypos: tabletLandscape.ypos,
    },
    desktop: {
      type: desktop.type,
      opacity: [desktop.opacity] as any,
      size: (Array.isArray(desktop.size) ? desktop.size : [desktop.size]) as any,
      rotate: [desktop.rotate] as any,
      inverted: desktop.inverted,
      xpos: desktop.xpos,
      ypos: desktop.ypos,
    },
    tv: {
      type: tv.type,
      opacity: [tv.opacity] as any,
      size: (Array.isArray(tv.size) ? tv.size : [tv.size]) as any,
      rotate: [tv.rotate] as any,
      inverted: tv.inverted,
      xpos: tv.xpos,
      ypos: tv.ypos,
    },
  }

  maskLayer.id = nextId
  return { layer: maskLayer, id: nextId + 1 }
}

/**
 * Check if a string is a simple color format (no layer prefix)
 */
function isSimpleColorFormat(format: string): boolean {
  return (
    !format.startsWith('color:') &&
    !format.startsWith('image:') &&
    !format.startsWith('video:') &&
    !format.startsWith('overlay:') &&
    !format.startsWith('pattern:') &&
    !format.startsWith('effect:') &&
    !format.startsWith('mask:')
  )
}

/**
 * Parse background format array to BackgroundLayer[]
 *
 * Format: Array of layer strings
 *
 * Simple format (backward compatible):
 *   ["solid:primary-40"] → color layer only
 *   ["gradient:primary-40|primary-80:to-right"] → color layer with gradient
 *
 * Multiple layers format:
 *   ["color:solid:primary-40", "image:url", "overlay:#000000:0.5"]
 *   ["color:adaptive:primary-80|primary-100", "image:url", "overlay:#000000:0.5:enabled"]
 *
 * Layer formats:
 *   - color:<color-format> - Color layer (e.g., "color:solid:primary-40" or "color:adaptive:primary-80|primary-100")
 *   - image:<url> or image:<url>:parallax - Image layer
 *   - video:<url> or video:<url>:loop:muted - Video layer
 *   - overlay:<color>:<opacity> or overlay:<color>:<opacity>:enabled - Overlay layer
 *   - pattern:<type>:<opacity>:<color> - Pattern layer
 *   - effect:<type>:<colors>:<bgColor>:<speed>:<opacity> - Effect layer
 *
 * Note: If a string doesn't start with a layer prefix, it's treated as a simple color format
 */
export function parseBackgroundLayers(layers: string[], templateLayers: BackgroundLayer[]): BackgroundLayer[] {
  if (!Array.isArray(layers) || layers.length === 0) {
    return templateLayers
  }

  const resultLayers: BackgroundLayer[] = []
  const modifiedTypes = new Set<string>()
  let nextId = Math.max(...templateLayers.map((l) => l.id), 0) + 1

  for (const layerStr of layers) {
    const trimmedLayer = layerStr.trim()
    if (!trimmedLayer) continue

    // If layer doesn't start with a prefix, treat as simple color format (backward compatible)
    if (isSimpleColorFormat(trimmedLayer)) {
      const { layer, id } = parseColorLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('color')
        resultLayers.push(layer)
        nextId = id
      }
      continue
    }

    // Parse layer with prefix
    if (trimmedLayer.startsWith('color:') || trimmedLayer.startsWith('gradient:')) {
      const { layer, id } = parseColorLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('color')
        resultLayers.push(layer)
        nextId = id
      }
    } else if (trimmedLayer.startsWith('image:')) {
      const { layer, id } = parseImageLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('image')
        resultLayers.push(layer)
        nextId = id
      }
    } else if (trimmedLayer.startsWith('video:')) {
      const { layer, id } = parseVideoLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('video')
        resultLayers.push(layer)
        nextId = id
      }
    } else if (trimmedLayer.startsWith('overlay:')) {
      const { layer, id } = parseOverlayLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('overlay')
        resultLayers.push(layer)
        nextId = id
      }
    } else if (trimmedLayer.startsWith('pattern:')) {
      const { layer, id } = parsePatternLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('pattern')
        resultLayers.push(layer)
        nextId = id
      }
    } else if (trimmedLayer.startsWith('effect:')) {
      const { layer, id } = parseEffectLayer(trimmedLayer, nextId)
      modifiedTypes.add('effect')
      resultLayers.push(layer)
      nextId = id
    } else if (trimmedLayer.startsWith('mask:')) {
      const { layer, id } = parseMaskLayer(trimmedLayer, templateLayers, nextId)
      if (layer) {
        modifiedTypes.add('mask')
        resultLayers.push(layer)
        nextId = id
      }
    }
  }

  // Merge with template layers: replace modified layers, keep unmodified ones
  // Preserve the original layer order from template
  const finalLayers: BackgroundLayer[] = []
  const processedTypes = new Set<string>()
  const modifiedLayersByType = new Map<string, BackgroundLayer>()

  // Index modified layers by type
  for (const layer of resultLayers) {
    modifiedLayersByType.set(layer.type, layer)
    processedTypes.add(layer.type)
  }

  // Preserve template layer order, replacing modified layers
  for (const templateLayer of templateLayers) {
    if (modifiedLayersByType.has(templateLayer.type)) {
      // Replace with modified layer
      finalLayers.push(modifiedLayersByType.get(templateLayer.type)!)
    } else {
      // Keep original template layer
      finalLayers.push(templateLayer)
    }
  }

  // Add any new layers that weren't in template (shouldn't happen, but handle gracefully)
  for (const layer of resultLayers) {
    if (!finalLayers.some((l) => l.type === layer.type)) {
      finalLayers.push(layer)
    }
  }

  return finalLayers
}
